/**
 * Reference Range Parsing and Validation Utilities
 * Handles various reference range formats and determines if values are out of range
 */

import moment from 'moment';

/**
 * Parses a reference range string and extracts min/max values
 * Supports formats like: "< 200", "> 40", "70-100", "4.0-11.0", "10 - 20", etc.
 * @param {string} referenceRange - The reference range string
 * @returns {Object} - Object with min, max, and type information
 */
export const parseReferenceRange = (referenceRange) => {
  if (!referenceRange || typeof referenceRange !== 'string') {
    return { min: null, max: null, type: 'unknown', original: referenceRange };
  }

  const range = referenceRange.trim();
  
  // Handle "< value" format (upper limit only)
  const lessThanMatch = range.match(/^<\s*([0-9]+\.?[0-9]*)$/);
  if (lessThanMatch) {
    return {
      min: null,
      max: parseFloat(lessThanMatch[1]),
      type: 'upper_limit',
      original: range
    };
  }

  // Handle "> value" format (lower limit only)
  const greaterThanMatch = range.match(/^>\s*([0-9]+\.?[0-9]*)$/);
  if (greaterThanMatch) {
    return {
      min: parseFloat(greaterThanMatch[1]),
      max: null,
      type: 'lower_limit',
      original: range
    };
  }

  // Handle "value1 - value2" format (range)
  const rangeMatch = range.match(/^([0-9]+\.?[0-9]*)\s*-\s*([0-9]+\.?[0-9]*)$/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2]),
      type: 'range',
      original: range
    };
  }

  // Handle "≤ value" format (upper limit with equals)
  const lessThanEqualMatch = range.match(/^≤\s*([0-9]+\.?[0-9]*)$/);
  if (lessThanEqualMatch) {
    return {
      min: null,
      max: parseFloat(lessThanEqualMatch[1]),
      type: 'upper_limit_inclusive',
      original: range
    };
  }

  // Handle "≥ value" format (lower limit with equals)
  const greaterThanEqualMatch = range.match(/^≥\s*([0-9]+\.?[0-9]*)$/);
  if (greaterThanEqualMatch) {
    return {
      min: parseFloat(greaterThanEqualMatch[1]),
      max: null,
      type: 'lower_limit_inclusive',
      original: range
    };
  }

  // Handle "value1 to value2" format
  const toRangeMatch = range.match(/^([0-9]+\.?[0-9]*)\s+to\s+([0-9]+\.?[0-9]*)$/i);
  if (toRangeMatch) {
    return {
      min: parseFloat(toRangeMatch[1]),
      max: parseFloat(toRangeMatch[2]),
      type: 'range',
      original: range
    };
  }

  // Handle single value (exact match expected)
  const singleValueMatch = range.match(/^([0-9]+\.?[0-9]*)$/);
  if (singleValueMatch) {
    const value = parseFloat(singleValueMatch[1]);
    return {
      min: value,
      max: value,
      type: 'exact',
      original: range
    };
  }

  // If no pattern matches, return unknown
  return {
    min: null,
    max: null,
    type: 'unknown',
    original: range
  };
};

/**
 * Determines if a lab value is out of the reference range
 * @param {number|string} value - The lab value to check
 * @param {string} referenceRange - The reference range string
 * @returns {Object} - Object with isAbnormal, flag, and details
 */
export const isValueOutOfRange = (value, referenceRange) => {
  // Convert value to number if it's a string
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Return normal if value is not a valid number
  if (isNaN(numericValue) || numericValue === null || numericValue === undefined) {
    return {
      isAbnormal: false,
      flag: null,
      reason: 'invalid_value',
      details: 'Value is not a valid number'
    };
  }

  // Parse the reference range
  const parsedRange = parseReferenceRange(referenceRange);
  
  // Return normal if range couldn't be parsed
  if (parsedRange.type === 'unknown') {
    return {
      isAbnormal: false,
      flag: null,
      reason: 'unknown_range',
      details: 'Reference range format not recognized'
    };
  }

  // Check against the parsed range
  switch (parsedRange.type) {
    case 'upper_limit': // "< value"
      if (numericValue >= parsedRange.max) {
        return {
          isAbnormal: true,
          flag: 'H',
          reason: 'above_upper_limit',
          details: `${numericValue} >= ${parsedRange.max} (should be < ${parsedRange.max})`
        };
      }
      break;

    case 'upper_limit_inclusive': // "≤ value"
      if (numericValue > parsedRange.max) {
        return {
          isAbnormal: true,
          flag: 'H',
          reason: 'above_upper_limit',
          details: `${numericValue} > ${parsedRange.max} (should be ≤ ${parsedRange.max})`
        };
      }
      break;

    case 'lower_limit': // "> value"
      if (numericValue <= parsedRange.min) {
        return {
          isAbnormal: true,
          flag: 'L',
          reason: 'below_lower_limit',
          details: `${numericValue} <= ${parsedRange.min} (should be > ${parsedRange.min})`
        };
      }
      break;

    case 'lower_limit_inclusive': // "≥ value"
      if (numericValue < parsedRange.min) {
        return {
          isAbnormal: true,
          flag: 'L',
          reason: 'below_lower_limit',
          details: `${numericValue} < ${parsedRange.min} (should be ≥ ${parsedRange.min})`
        };
      }
      break;

    case 'range': // "value1 - value2"
      if (numericValue < parsedRange.min) {
        return {
          isAbnormal: true,
          flag: 'L',
          reason: 'below_range',
          details: `${numericValue} < ${parsedRange.min} (normal range: ${parsedRange.min}-${parsedRange.max})`
        };
      }
      if (numericValue > parsedRange.max) {
        return {
          isAbnormal: true,
          flag: 'H',
          reason: 'above_range',
          details: `${numericValue} > ${parsedRange.max} (normal range: ${parsedRange.min}-${parsedRange.max})`
        };
      }
      break;

    case 'exact': // Single value
      if (numericValue !== parsedRange.min) {
        const flag = numericValue > parsedRange.min ? 'H' : 'L';
        return {
          isAbnormal: true,
          flag: flag,
          reason: 'not_exact',
          details: `${numericValue} ≠ ${parsedRange.min} (expected exactly ${parsedRange.min})`
        };
      }
      break;
  }

  // If we get here, the value is within normal range
  return {
    isAbnormal: false,
    flag: 'N',
    reason: 'within_range',
    details: 'Value is within normal range'
  };
};

/**
 * Gets the most recent lab values that are currently out of range
 * @param {Array} labEntries - Array of all lab entries
 * @returns {Array} - Array of lab entries that are currently out of range
 */
export const getCurrentlyAbnormalLabs = (labEntries) => {
  if (!Array.isArray(labEntries)) return [];

  // Group entries by parameter name and get the most recent for each
  const mostRecentByParameter = {};

  labEntries.forEach(entry => {
    if (!entry.name || (entry.value === null || entry.value === undefined)) return;

    const currentMostRecent = mostRecentByParameter[entry.name];
    if (!currentMostRecent || new Date(entry.entryDate) > new Date(currentMostRecent.entryDate)) {
      mostRecentByParameter[entry.name] = entry;
    }
  });

  // Check each most recent value against its reference range
  const currentlyAbnormalLabs = [];

  Object.values(mostRecentByParameter).forEach(entry => {
    if (!entry.referenceRange) return; // Skip if no reference range

    const rangeCheck = isValueOutOfRange(entry.value, entry.referenceRange);

    if (rangeCheck.isAbnormal) {
      currentlyAbnormalLabs.push({
        ...entry,
        computedFlag: rangeCheck.flag,
        abnormalReason: rangeCheck.reason,
        abnormalDetails: rangeCheck.details,
        isCurrentlyAbnormal: true
      });
    }
  });

  // Sort by date descending (most recent first)
  return currentlyAbnormalLabs.sort((a, b) =>
    new Date(b.entryDate) - new Date(a.entryDate)
  );
};

/**
 * Formats abnormal details for display
 * @param {Object} lab - Lab entry with abnormal details
 * @returns {string} - Formatted string for display
 */
export const formatAbnormalDetails = (lab) => {
  if (!lab.computedFlag) return '';
  
  const flag = lab.computedFlag;
  const value = lab.value;
  const range = lab.referenceRange;
  
  if (flag === 'H') {
    return `High (${value} above normal range: ${range})`;
  } else if (flag === 'L') {
    return `Low (${value} below normal range: ${range})`;
  }
  
  return `Abnormal (${value}, normal: ${range})`;
};
